import React, { useState, useEffect } from 'react';
import { leaveAPI, userAPI } from '../services/api';
import SearchableEmployeeSelect from './SearchableEmployeeSelect';
import styled from 'styled-components';

const FormContainer = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
`;

const Title = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: bold;
  color: #555;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    border-color: #007bff;
    outline: none;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    border-color: #007bff;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    border-color: #007bff;
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &.primary {
    background-color: #007bff;
    color: white;
    
    &:hover {
      background-color: #0056b3;
    }
    
    &:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background-color: #6c757d;
    color: white;
    
    &:hover {
      background-color: #545b62;
    }
  }
`;

const LeaveRequestsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 16px;
`;

const LeaveRequestItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
`;

const LeaveInfo = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-weight: bold;
  color: #333;
`;

const LeaveDetails = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  background-color: ${props => {
    switch(props.status) {
      case 'approved': return '#d4edda';
      case 'rejected': return '#f8d7da';
      case 'pending': return '#fff3cd';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'approved': return '#155724';
      case 'rejected': return '#721c24';
      case 'pending': return '#856404';
      default: return '#6c757d';
    }
  }};
  margin-right: 8px;
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  margin-left: 4px;
  
  &.approve {
    background-color: #28a745;
    color: white;
    
    &:hover {
      background-color: #218838;
    }
  }
  
  &.reject {
    background-color: #dc3545;
    color: white;
    
    &:hover {
      background-color: #c82333;
    }
  }
`;

const LeaveManagementForm = ({ selectedDate, onSave, onCancel }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form state for creating new leave
  const [newLeaveForm, setNewLeaveForm] = useState({
    user_id: '',
    leave_type_id: '',
    reason: ''
  });

  // Helper function to create timezone-safe date string
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchLeaveTypes();
    fetchEmployees();
    fetchLeaveRequestsForDate();
  }, [selectedDate]);

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveAPI.getLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await userAPI.getAll();
      setEmployees(response.data.filter(user => user.is_active));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLeaveRequestsForDate = async () => {
    if (!selectedDate) return;
    
    try {
      const dateStr = getLocalDateString(selectedDate);
      const response = await leaveAPI.getLeaveRequests();
      
      // Filter approved requests for the selected date (all admin-created leave is auto-approved)
      const dayRequests = response.data.filter(request => 
        request.leave_date === dateStr && request.status === 'approved'
      );
      
      setLeaveRequests(dayRequests);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    }
  };

  const handleCreateLeaveRequest = async () => {
    if (!newLeaveForm.user_id || !newLeaveForm.leave_type_id) {
      alert('Please select both employee and leave type');
      return;
    }

    setLoading(true);
    try {
      // Format date as YYYY-MM-DD for the API (timezone-safe)
      const dateStr = getLocalDateString(selectedDate);
      
      // Create the leave request with auto-approval
      const createResponse = await leaveAPI.createLeaveRequest({
        user_id: parseInt(newLeaveForm.user_id),
        leave_type_id: parseInt(newLeaveForm.leave_type_id),
        leave_date: dateStr,
        reason: newLeaveForm.reason,
        status: 'approved' // Auto-approve when created by admin
      });

      // Refresh the list (no need for separate approval call)
      await fetchLeaveRequestsForDate();
      
      // Reset form
      setNewLeaveForm({
        user_id: '',
        leave_type_id: '',
        reason: ''
      });
      
      if (onSave) onSave();
    } catch (error) {
      console.error('Error creating leave:', error);
      alert('Error creating leave: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeave = async (requestId) => {
    setLoading(true);
    try {
      await leaveAPI.deleteLeaveRequest(requestId);
      await fetchLeaveRequestsForDate();
      if (onSave) onSave(); // Refresh calendar
    } catch (error) {
      console.error('Error deleting leave:', error);
      alert('Error deleting leave: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <FormContainer>
      <Title>
        Leave Management - {selectedDate ? formatDate(selectedDate) : 'Select Date'}
      </Title>

      {/* Current Leave for this Date */}
      {leaveRequests.length > 0 && (
        <>
          <h4 style={{ marginBottom: '12px', color: '#495057' }}>Current Leave for this Date:</h4>
          <LeaveRequestsList>
            {leaveRequests.map(request => (
              <LeaveRequestItem key={request.id}>
                <LeaveInfo>
                  <EmployeeName>{request.user?.name}</EmployeeName>
                  <LeaveDetails>
                    {request.leave_type?.name} - {request.reason || 'No reason provided'}
                  </LeaveDetails>
                </LeaveInfo>
                <div>
                  <ActionButton 
                    className="reject"
                    onClick={() => handleDeleteLeave(request.id)}
                    disabled={loading}
                  >
                    Remove
                  </ActionButton>
                </div>
              </LeaveRequestItem>
            ))}
          </LeaveRequestsList>
        </>
      )}

      {/* Add New Leave Form */}
      <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#495057' }}>Add New Leave:</h4>
      
      <FormGroup>
        <Label>Employee</Label>
        <SearchableEmployeeSelect
          employees={employees}
          value={newLeaveForm.user_id}
          onChange={(e) => setNewLeaveForm({...newLeaveForm, user_id: e.target.value})}
          placeholder="Search for employee..."
          displayFn={(emp) => `${emp.name} - ${emp.role}`}
        />
      </FormGroup>

      <FormGroup>
        <Label>Leave Type</Label>
        <Select
          value={newLeaveForm.leave_type_id}
          onChange={(e) => setNewLeaveForm({...newLeaveForm, leave_type_id: e.target.value})}
        >
          <option value="">Select Leave Type</option>
          {leaveTypes.map(type => (
            <option key={type.id} value={type.id}>
              {type.name} ({type.quota_type})
            </option>
          ))}
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Reason (Optional)</Label>
        <TextArea
          value={newLeaveForm.reason}
          onChange={(e) => setNewLeaveForm({...newLeaveForm, reason: e.target.value})}
          placeholder="Enter reason for leave..."
        />
      </FormGroup>

      <ButtonGroup>
        <Button className="secondary" onClick={onCancel}>
          Close
        </Button>
        <Button 
          className="primary" 
          onClick={handleCreateLeaveRequest}
          disabled={loading || !newLeaveForm.user_id || !newLeaveForm.leave_type_id}
        >
          {loading ? 'Adding...' : 'Add Leave'}
        </Button>
      </ButtonGroup>
    </FormContainer>
  );
};

export default LeaveManagementForm;